import * as THREE from 'three';
import { decodeWindVector, getWindColor } from '../constants.js';

const imageDataCache = new WeakMap();

export function createClickHandler({
  camera,
  domElement,
  raycaster,
  mouse,
  planetMesh,
  getVelocityTexture,
  getWindMagnitudeLimit,
  getWindMagnitudeRange,
  onPointSelected,
}) {
  return function onPointerDown(event) {
    if (event.button !== 0) {
      return;
    }

    const velocityTexture = getVelocityTexture();
    if (!velocityTexture?.image) {
      return;
    }

    const bounds = domElement.getBoundingClientRect();
    mouse.x = ((event.clientX - bounds.left) / bounds.width) * 2 - 1;
    mouse.y = -(((event.clientY - bounds.top) / bounds.height) * 2 - 1);

    raycaster.setFromCamera(mouse, camera);
    const intersects = raycaster.intersectObject(planetMesh, false);

    if (!intersects.length) {
      return;
    }

    onPointSelected?.(sampleWindAtWorldPoint({
      point: intersects[0].point,
      planetMesh,
      velocityTexture,
      windMagnitudeRange: getWindMagnitudeRange?.(),
      windMagnitudeLimit: getWindMagnitudeLimit?.(),
    }));
  };
}

export function sampleWindAtWorldPoint({
  point,
  planetMesh,
  velocityTexture,
  windMagnitudeRange,
  windMagnitudeLimit,
}) {
  if (!point || !planetMesh || !velocityTexture?.image) {
    return null;
  }

  const localPoint = planetMesh.worldToLocal(point.clone()).normalize();
  const longitudeRadians = Math.atan2(localPoint.z, localPoint.x);
  const latitudeRadians = Math.asin(THREE.MathUtils.clamp(localPoint.y, -1, 1));
  const lon = (longitudeRadians + Math.PI) / (Math.PI * 2);
  const lat = (latitudeRadians + (Math.PI / 2)) / Math.PI;
  const sample = getColourAtUV(velocityTexture, { x: lon, y: lat });
  const { lon: zonalWind, lat: meridionalWind, magnitude } = decodeWindVector(sample);

  return {
    mag: Math.round(magnitude * 100) / 100,
    longitude: Math.round(THREE.MathUtils.radToDeg(longitudeRadians) * 100) / 100,
    latitude: Math.round(THREE.MathUtils.radToDeg(latitudeRadians) * 100) / 100,
    zonalWind: Math.round(zonalWind * 100) / 100,
    meridionalWind: Math.round(meridionalWind * 100) / 100,
    colour: getWindColor(magnitude, windMagnitudeRange ?? windMagnitudeLimit),
  };
}

function getColourAtUV(texture, uv) {
  const imageData = getImageData(texture.image);
  const width = imageData.width;
  const height = imageData.height;

  const x = Math.max(0, Math.min(Math.floor(uv.x * (width - 1)), width - 1));
  const y = Math.max(0, Math.min(Math.floor((1 - uv.y) * (height - 1)), height - 1));
  const index = (y * width + x) * 4;

  return {
    r: imageData.data[index] / 255,
    g: imageData.data[index + 1] / 255,
    b: imageData.data[index + 2] / 255,
    a: imageData.data[index + 3] / 255,
  };
}

function getImageData(image) {
  if (imageDataCache.has(image)) {
    return imageDataCache.get(image);
  }

  const canvas = document.createElement('canvas');
  canvas.width = image.width;
  canvas.height = image.height;

  const context = canvas.getContext('2d', { willReadFrequently: true });
  context.drawImage(image, 0, 0);

  const imageData = context.getImageData(0, 0, image.width, image.height);
  imageDataCache.set(image, imageData);

  return imageData;
}
